import React from 'react';
import { NavigateFunction } from 'react-router-dom';

import { Button, Chip, Icon } from '@column/column-ui-kit';
import { type DataTableColumnDef, DropdownMenuItem } from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { PopoverFilterEntry } from '~/components';
import { PaginatedDataTable, getTimestampColumn, useTimeFormat } from '~/components/Table';
import { TableActionDropdown } from '~/lib/components/table/TableActionDropdown';
import { useNavigate } from '~/lib/navigation';
import { TransferAmount } from '~/lib/transfers';
import { LoanRepository } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';

const getDisbursementStatusChip = (status: string | undefined) => {
  if (!status) return null;

  const statusLower = status.toLowerCase();

  if (statusLower === 'completed') {
    return (
      <Chip icon={<Icon.CircleCheck />} variant="subtle" type="success">
        Completed
      </Chip>
    );
  }
  if (statusLower === 'cancelled' || statusLower === 'canceled') {
    return (
      <Chip icon={<Icon.CircleCross />} variant="subtle" type="danger">
        Cancelled
      </Chip>
    );
  }
  if (statusLower === 'hold') {
    return (
      <Chip icon={<Icon.Warning />} variant="subtle" type="warning">
        Hold
      </Chip>
    );
  }

  return (
    <Chip variant="subtle" type="default">
      {status}
    </Chip>
  );
};

export const handleFetchDisbursements = async (params: any, navigate: NavigateFunction, addLoanLink?: boolean) => {
  return LoanRepository.getAllDisbursements(params).then((response) => {
    const entries: any = [];

    if (response.disbursements.length) {
      response.disbursements.map((entry: any) => {
        entries.push({
          id: entry.id,
          description: [entry?.amount ?? 0, entry.description, entry.currency],
          status: entry.status,
          created: entry.createdAt,
          action: (
            <TableActionDropdown>
              {addLoanLink && (
                <DropdownMenuItem onSelect={() => navigate(`${ROUTE.LOANS}/edit/${entry.loanId}`)}>
                  Loan
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => navigate(`${ROUTE.LOANS}/disbursements/edit/${entry.id}`)}>
                Edit
              </DropdownMenuItem>
            </TableActionDropdown>
          ),
        });
      });
    }
    return { entries, hasMore: Boolean(response?.hasMore) };
  });
};

export const getDisbursementColumns = (
  timeFormat: TableTimeFormat,
  onTimeFormatChange: (value: TableTimeFormat) => void
): DataTableColumnDef<Record<string, any>>[] => [
  {
    header: 'Amount / Description',
    accessor: 'description',
    width: 'minmax(300px, 1fr)',
    className: 'min-w-0',
    cell: ({ value }) => (
      <TransferAmount amount={Number(value[0] ?? 0)} currencyCode={value[2]} description={value[1] ?? undefined} />
    ),
  },
  {
    header: 'Status',
    width: 'minmax(min-content, 160px)',
    cell: ({ value }) => getDisbursementStatusChip(value),
    accessor: 'status',
  },
  getTimestampColumn<Record<string, any>>({ accessor: 'created', timeFormat, onTimeFormatChange, width: '160px' }),
  {
    header: '',
    width: 'min-content',
    accessor: 'action',
    cell: ({ value }) => value,
  },
];

export const disbursementFilter: PopoverFilterEntry[] = [
  {
    id: 'created',
    label: 'Created',
    type: 'date',
  },
];

export const PageLoansDisbursementOverview: React.FC = () => {
  const navigate = useNavigate();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('disbursements');

  return (
    <>
      <PageActions>
        <Button onClick={() => navigate(ROUTE.LOANS)} icon={<Icon.HandCoins />} size="small" variant="secondary">
          Loans
        </Button>
        <Button onClick={() => navigate(`${ROUTE.LOANS}/disbursements/create`)} icon={<Icon.Plus />} size="small">
          Create Disbursement
        </Button>
      </PageActions>

      <PaginatedDataTable<Record<string, any>>
        tableId="disbursements"
        fetch={(params) => handleFetchDisbursements(params, navigate, true)}
        filter={disbursementFilter}
        empty={{
          icon: <Icon.AnimationHandCoins />,
          title: 'No disbursements found',
          action: (
            <Button onClick={() => navigate(`${ROUTE.LOANS}/disbursements/create`)} size="small" icon={<Icon.Plus />}>
              Create Disbursement
            </Button>
          ),
        }}
        columns={getDisbursementColumns(timeFormat, onTimeFormatChange)}
        onRowClick={(row) => {
          navigate(`${ROUTE.LOANS}/disbursements/edit/${row.id}`);
        }}
      />
    </>
  );
};
